import React, { type ReactNode } from 'react';

import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { token } from '@atlaskit/tokens';
import { CreditsInlineMessage } from '@atlassian/rovo-credits/credits-inline-message';
import { CreditsProvider, useCreditsState } from '@atlassian/rovo-credits/credits-provider';

const styles = cssMap({
	frame: {
		width: 'max-content',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('elevation.surface.sunken'),
		backgroundClip: 'padding-box',
	},
	message: {
		boxSizing: 'border-box',
		width: '220px',
	},
});

const AgentProfileCardCreditsContent = ({
	children,
	agentName,
}: {
	children: ReactNode;
	agentName?: string;
}) => {
	const { bannerState } = useCreditsState();
	if (bannerState?.balanceState !== 'depleted') {
		return <>{children}</>;
	}
	return (
		<Box padding="space.100" xcss={styles.frame}>
			<Stack space="space.150">
				{children}
				<Box paddingInline="space.050" paddingBlockEnd="space.050" xcss={styles.message}>
					<CreditsInlineMessage featureName="agent-profile-card" agentName={agentName} />
				</Box>
			</Stack>
		</Box>
	);
};

/** Frames the profile card with shared credits messaging when credits are depleted. */
export const AgentProfileCardCreditsWrapper = ({
	children,
	cloudId,
	agentName,
}: {
	children: ReactNode;
	cloudId: string;
	agentName?: string;
}): React.JSX.Element => {
	if (!fg('rovo_credits_degradation_ui')) {
		return <>{children}</>;
	}
	return (
		<CreditsProvider product="atlassian-agent-studio" cloudId={cloudId} sessionScope="site">
			<AgentProfileCardCreditsContent agentName={agentName}>
				{children}
			</AgentProfileCardCreditsContent>
		</CreditsProvider>
	);
};
